import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { get, isEmpty, isEqual } from 'lodash';
import { currentCompanyId } from 'utils/company';
import { activeReport } from 'utils/report';
import debug from 'utils/debug';

import * as C from 'actions/constants';
import { getSearchString } from 'utils/urlOperations';
import { authPresent, userHasValidAuthToken } from 'utils/session';
import { clearSessionStorage } from 'utils/stateStorage';
import * as SESSION_ACTIONS from 'actions/session';
import * as COMPANIES_ACTIONS from 'actions/companies';
import * as REPORTS_ACTIONS from 'actions/reports';

function authenticate(WrappedComponent) {
  class AuthHOC extends React.Component {
    constructor(props) {
      super(props);
      this.evaluateRedirections = this.evaluateRedirections.bind(this);
      this.parseQueryParams = this.parseQueryParams.bind(this);
      this.isValidToken = this.isValidToken.bind(this);
      this.autoSignIn = this.autoSignIn.bind(this);
      this.checkAuth = this.checkAuth.bind(this);
      this.setActiveCompany = this.setActiveCompany.bind(this);
      this.checkSingleCompany = this.checkSingleCompany.bind(this);
      this.setActiveReport = this.setActiveReport.bind(this);
      this.setReport = this.setReport.bind(this);
      this.checkSingleReport = this.checkSingleReport.bind(this);
      this.triggerRedirection = this.triggerRedirection.bind(this);
      this.whitelistedPage = this.whitelistedPage.bind(this);
      this.handleRedirection = this.handleRedirection.bind(this);

      this.state = {
        e: null,
        p: null,
        c: null,
        r: null,
        redirectTo: null,
        nextPath: null,
        triggerRedirect: false,
      };
    }

    componentDidMount() {
      this.evaluateRedirections();
    }

    componentDidUpdate(prevProps, prevState) {
      const { history, location } = this.props;
      const { history: prevHistory, location: prevLocation } = prevProps;
      const { triggerRedirect } = this.state;
      const { triggerRedirect: prevTriggerRedirect } = prevState;

      if (triggerRedirect && !prevTriggerRedirect) {
        this.handleRedirection();
      }

      if (!isEqual({ history, location }, { history: prevHistory, location: prevLocation })) {
        this.evaluateRedirections();
      }
    }

    setActiveCompany() {
      const { getCompany, setCompany, location: { pathname } } = this.props;
      const { c: companyQueryParam } = this.state;
      const currentCompany = currentCompanyId();

      if (isEmpty(companyQueryParam)) {
        if (isEqual(pathname, '/companies')) {
          return new Promise((_resolve, reject) => reject());
        }

        if (isEmpty(currentCompany)) {
          return this.checkSingleCompany();
        }

        if (isEqual(pathname, '/')) {
          const nextPath = '/login';
          return new Promise((_resolve, reject) => this.setState({ nextPath }, reject));
        }

        if (isEqual(pathname, '/login')) {
          const nextPath = '/reports';
          return new Promise((resolve) => this.setState({ nextPath }, resolve));
        }

        return true;
      }

      if (!isEqual(currentCompany, companyQueryParam)) {
        return getCompany(companyQueryParam)
          .then((res) => setCompany(res.data))
          .catch(() => this.checkSingleCompany());
      }

      return true;
    }

    setReport({ id: reportId }) {
      const { getReport } = this.props;

      return getReport(reportId)
        .then((res) => {
          let nextPath = `/reports/${res.data.report.id}`;

          if (isEmpty(get(res, 'data.report'))) {
            nextPath = '/reports';
          }

          return new Promise((resolve) => (
            this.setState({ nextPath }, resolve)
          ));
        });
    }

    setActiveReport() {
      const { getReport, location: { pathname } } = this.props;
      const { r: reportQueryParam } = this.state;
      const currentCompany = currentCompanyId();
      const currentReport = activeReport();

      if (!currentCompany) {
        return new Promise((_resolve, reject) => reject());
      }

      if (isEmpty(reportQueryParam)) {
        if (pathname.startsWith('/reports')) {
          return new Promise((_resolve, reject) => reject());
        }

        if (isEmpty(currentReport)) {
          return this.checkSingleReport();
        }

        if (isEqual(pathname, '/')) {
          const nextPath = '/login';
          return new Promise((_resolve, reject) => this.setState({ nextPath }, reject));
        }

        if (isEqual(pathname, '/login')) {
          const nextPath = `/reports/${currentReport.id}`;
          return new Promise((resolve) => this.setState({ nextPath }, resolve));
        }

        return true;
      }

      if (!isEqual(get(currentReport, 'id'), reportQueryParam)) {
        return getReport(reportQueryParam)
          .then((res) => this.setReport(res.data.report))
          .catch(() => this.checkSingleReport());
      }

      return true;
    }

    checkSingleCompany() {
      const { loadCompanies, setCompany } = this.props;
      return loadCompanies().then((companies) => {
        let nextPath = '/companies';

        if (companies.data.length === 1) {
          setCompany(companies.data[0]);
          nextPath = '/reports';
        }

        return new Promise((res) => (
          this.setState({ nextPath }, res)
        ));
      });
    }

    checkSingleReport() {
      const { loadReports, location: { pathname } } = this.props;
      return loadReports().then((reports) => {
        if (reports.data.count === 1) {
          return this.setReport(reports.data.list[0]);
        }

        /*const nextPath = isEqual(pathname, '/reports') ? null : '/reports';
        return new Promise((res) => (
          this.setState({ nextPath }, res)
        ));*/
      });
    }

    evaluateRedirections() {
      return this.parseQueryParams()
        .then(this.checkAuth)
        .then(this.setActiveCompany)
        .then(this.setActiveReport)
        .catch((e) => e && debug(`Error when defining next path: ${e}`))
        .finally(this.handleRedirection);
    }

    parseQueryParams() {
      const { history } = this.props;
      const e = getSearchString(history, 'e');
      const p = getSearchString(history, 'p');
      const c = getSearchString(history, 'c');
      const r = getSearchString(history, 'r');
      const redirectTo = getSearchString(history, 'redirect_to');
      return new Promise((resolve) => (
        this.setState({
          e, p, c, r, redirectTo,
        }, resolve)
      ));
    }

    isValidToken() {
      const { auth } = this.props;
      return userHasValidAuthToken(auth);
    }

    autoSignIn(e, p) {
      const { signIn } = this.props;

      return signIn({
        username: atob(e),
        password: atob(p),
        scope: 'user',
        grant_type: 'password',
      });
    }

    checkAuth() {
      const {
        auth,
        location: { pathname },
        refreshToken,
      } = this.props;
      const { e, p } = this.state;

      if (!isEmpty(e) && !isEmpty(p)) {
        clearSessionStorage();
        return this.autoSignIn(e, p);
      }

      return new Promise((resolve, reject) => {
        if (!authPresent()) {
          if (!isEqual(pathname, '/')) {
            return this.setState({ nextPath: '/' }, reject);
          }
          return reject();
        }

        if (this.isValidToken()) {
          return resolve();
        }

        const token = get(auth, 'refresh_token');
        return refreshToken(token)
          .then((res) => {
            if (res.type === C.REFRESH_TOKEN_ERROR) {
              clearSessionStorage();
              return this.setState({ nextPath: '/' }, reject);
            }
            return resolve();
          })
          .catch(() => {
            clearSessionStorage();
            return this.setState({ nextPath: '/' }, reject);
          });
      });
    }

    triggerRedirection() {
      this.setState({ triggerRedirect: true });
    }

    whitelistedPage() {
      const { location: { pathname } } = this.props;
      return ['/exports'].includes(pathname);
    }

    handleRedirection() {
      const { history } = this.props;
      const { nextPath, redirectTo } = this.state;

      const effectiveNextPath = isEmpty(redirectTo) ? nextPath : redirectTo;

      if (isEmpty(effectiveNextPath) || this.whitelistedPage()) {
        return;
      }

      history.push(effectiveNextPath);
      this.setState({ triggerRedirect: false, nextPath: null, redirectTo: null });
    }

    render() {
      const { ...props } = this.props;
      return (
        <WrappedComponent
          {...props}
        />
      );
    }
  }

  AuthHOC.defaultProps = {
    auth: {},
  };

  AuthHOC.propTypes = {
    auth: PropTypes.object, // eslint-disable-line
    history: PropTypes.object.isRequired, // eslint-disable-line
    location: PropTypes.object.isRequired, // eslint-disable-line
    signIn: PropTypes.func.isRequired,
    refreshToken: PropTypes.func.isRequired,
    loadCompanies: PropTypes.func.isRequired,
    getCompany: PropTypes.func.isRequired,
    setCompany: PropTypes.func.isRequired,
    loadReports: PropTypes.func.isRequired,
    getReport: PropTypes.func.isRequired,
  };

  function mapStateToProps(state) {
    return {
      auth: state.session.data,
    };
  }

  const mapDispatchToProps = {
    signIn: SESSION_ACTIONS.signIn,
    refreshToken: SESSION_ACTIONS.refreshToken,
    loadCompanies: COMPANIES_ACTIONS.load,
    getCompany: COMPANIES_ACTIONS.get,
    setCompany: COMPANIES_ACTIONS.setCompany,
    loadReports: REPORTS_ACTIONS.load,
    getReport: REPORTS_ACTIONS.get,
  };

  return connect(mapStateToProps, mapDispatchToProps)(withRouter(AuthHOC));
}

export default authenticate;
